import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard } from "../../../components/home/functions";
import { statusObject } from "../../../components/utils/allProductStatus";
import {
  ACTIVE_QUEUE_STATUSES,
  INCOMING_STATUSES,
  ANALYZING_STATUSES,
  getUrgencyMeta,
  formatDate,
  describeDaysLeft,
  formatItems,
} from "../../../components/home/dashboardView";
import MobileSection from "../../ui/MobileSection";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileError from "../../ui/MobileError";

function UrgencyBadge({ urgency }) {
  const meta = getUrgencyMeta(urgency);
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

function SummaryTile({ title, bucket, accent, onClick }) {
  const { items, ordersLabel } = formatItems(bucket);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[72px] flex-col items-start justify-center rounded-xl border p-3 text-left ${accent ?? "border-primary/30 bg-mgs-surface"}`}
    >
      <span className="text-xs text-laranjinha/70">{title}</span>
      <span className="text-2xl font-bold text-laranjinha">{items}</span>
      <span className="text-[11px] text-laranjinha/60">itens · {ordersLabel}</span>
    </button>
  );
}

export default function MobileHome({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [clientSearch, setClientSearch] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await getDashboard(user.token);
    if (response?.type === "success") {
      setData(response.data);
    } else {
      setError(response?.message ?? "Não foi possível carregar o painel.");
    }
    setHasLoaded(true);
    setLoading(false);
  }, [user.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const goToClientSearch = () => {
    const term = clientSearch.trim();
    const preset = term.includes("@") ? { presetEmail: term } : { presetName: term };
    navigate("/manafix/users", { state: preset });
  };

  const goToQueue = () => {
    navigate("/manafix/products", { state: { presetStatus: ACTIVE_QUEUE_STATUSES } });
  };

  const goToIncoming = () => {
    navigate("/manafix/products", { state: { presetStatus: INCOMING_STATUSES } });
  };

  const goToCreateOrder = () => {
    navigate("/manafix/products/create", { state: null });
  };

  const openProduct = (id) => {
    navigate(`/manafix/products/${id}`);
  };

  if (loading && !hasLoaded) {
    return (
      <div aria-label="Carregando painel" role="status">
        <MobileSkeleton lines={8} />
      </div>
    );
  }

  if (error && !data) {
    return <MobileError message={error} onRetry={loadData} />;
  }

  const goToAnalyzing = () => {
    navigate("/manafix/products", { state: { presetStatus: ANALYZING_STATUSES } });
  };

  const queue = data?.queue;
  const priorityQueue = data?.priorityQueue ?? [];
  const nextOrder = priorityQueue[0];
  const analyzing = data?.analyzing;

  return (
    <div className="flex flex-col pb-6">
      <div className="flex items-center justify-end border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50"
        >
          {loading ? "Atualizando…" : "Atualizar"}
        </button>
      </div>

      <MobileSection title="Ações rápidas">
        <form
          className="flex items-center gap-2 py-2"
          onSubmit={(e) => { e.preventDefault(); goToClientSearch(); }}
        >
          <input
            className="min-h-[44px] w-full rounded-lg border border-primary/30 bg-mgs-surface px-3 text-laranjinha placeholder-laranjinha/40 outline-none"
            type="text"
            placeholder="Buscar cliente…"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
          />
          <button
            type="submit"
            className="min-h-[44px] whitespace-nowrap rounded-lg bg-primary px-3 text-sm font-semibold text-black"
          >
            Buscar
          </button>
        </form>
        <div className="grid grid-cols-2 gap-2 pb-2">
          <button
            type="button"
            onClick={goToQueue}
            className="min-h-[44px] rounded-lg border border-primary/40 bg-mgs-surface px-3 text-sm font-medium text-laranjinha"
          >
            Fila de pedidos
          </button>
          <button
            type="button"
            onClick={goToIncoming}
            className="min-h-[44px] rounded-lg border border-primary/40 bg-mgs-surface px-3 text-sm font-medium text-laranjinha"
          >
            Pedidos a receber
          </button>
          <button
            type="button"
            onClick={goToCreateOrder}
            className="col-span-2 min-h-[44px] rounded-lg border border-primary/40 bg-mgs-surface px-3 text-sm font-medium text-laranjinha"
          >
            + Novo pedido
          </button>
        </div>
      </MobileSection>

      <MobileSection title="Resumo da operação">
        <div className="grid grid-cols-2 gap-2 py-2">
          <SummaryTile
            title="Atrasados"
            bucket={queue?.overdue}
            accent={queue?.overdue?.orders ? "border-red-600 bg-red-950/40" : undefined}
            onClick={goToQueue}
          />
          <SummaryTile title="Próx. 7 dias" bucket={queue?.dueIn7} onClick={goToQueue} />
          <SummaryTile title="Próx. 15 dias" bucket={queue?.dueIn15} onClick={goToQueue} />
          <SummaryTile title="Próx. 30 dias" bucket={queue?.dueIn30} onClick={goToQueue} />
          <SummaryTile title="Total na fila" bucket={queue?.total} onClick={goToQueue} />
        </div>
      </MobileSection>

      <MobileSection title="Próximo pedido">
        {nextOrder ? (
          <button
            type="button"
            onClick={() => openProduct(nextOrder.id)}
            className="my-2 w-full rounded-xl border border-primary bg-mgs-elevated p-3 text-left"
          >
            <div className="mb-1 flex items-center gap-2">
              <UrgencyBadge urgency={nextOrder.urgency} />
              <span className="text-xs text-laranjinha/70">
                {statusObject[nextOrder.status] ?? nextOrder.status}
              </span>
            </div>
            <p className="text-lg font-bold text-laranjinha">{nextOrder.userName ?? "Cliente sem nome"}</p>
            <p className="text-xs text-laranjinha/70">
              {nextOrder.numberOfCards} itens · prazo {formatDate(nextOrder.dueDate)} · {describeDaysLeft(nextOrder.daysLeft)}
            </p>
          </button>
        ) : (
          <p className="py-3 text-sm text-laranjinha/60">Nenhum pedido na fila de produção. 🎉</p>
        )}
        {priorityQueue.slice(1, 6).map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => openProduct(entry.id)}
            className="flex w-full items-center justify-between gap-2 border-b border-primary/20 py-3 text-left last:border-b-0"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-laranjinha">{entry.userName ?? "Cliente sem nome"}</p>
              <p className="truncate text-xs text-laranjinha/60">
                {entry.numberOfCards} itens · {describeDaysLeft(entry.daysLeft)}
              </p>
            </div>
            <UrgencyBadge urgency={entry.urgency} />
          </button>
        ))}
      </MobileSection>

      <MobileSection title="Em análise">
        <p className="pt-2 text-[11px] text-laranjinha/50">
          {analyzing?.total?.items ?? 0} itens · {formatItems(analyzing?.total).ordersLabel} — fora da fila de produção
        </p>
        {analyzing?.total?.orders ? (
          (analyzing.entries ?? []).slice(0, 5).map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => openProduct(entry.id)}
              className="flex w-full items-center justify-between gap-2 border-b border-primary/10 py-3 text-left last:border-b-0"
            >
              <span className="truncate text-sm text-laranjinha/70">{entry.userName ?? "Cliente sem nome"}</span>
              <span className="whitespace-nowrap text-xs text-laranjinha/50">
                {entry.numberOfCards} itens · {describeDaysLeft(entry.daysLeft)}
              </span>
            </button>
          ))
        ) : (
          <p className="py-3 text-sm text-laranjinha/50">Nenhum pedido em análise.</p>
        )}
        <button
          type="button"
          onClick={goToAnalyzing}
          className="min-h-[44px] w-full pt-1 text-left text-sm text-laranjinha/60"
        >
          Ver todos →
        </button>
      </MobileSection>

      <MobileSection title="A caminho da Manafix">
        {INCOMING_STATUSES.map((status) => {
          const { items, ordersLabel } = formatItems(data?.incoming?.byStatus?.[status]);
          return (
            <div
              key={status}
              className="flex items-center justify-between gap-4 border-b border-primary/20 py-3 last:border-b-0"
            >
              <span className="text-sm text-laranjinha/70">{statusObject[status]}</span>
              <span className="text-sm font-semibold text-laranjinha">
                {items} itens · {ordersLabel}
              </span>
            </div>
          );
        })}
        <p className="pt-3 text-[11px] text-laranjinha/50">
          {data?.totals?.users ?? "—"} usuários · {data?.totals?.serials ?? "—"} seriais
        </p>
      </MobileSection>
    </div>
  );
}
